import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Sparkles, CheckCircle2 } from 'lucide-react';
interface Props {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  onDecline: () => void;
}
const BG_IMAGE = 'https://cdn.magicpatterns.com/uploads/r7VbD2yCinmF3hAebSSDnf/BONO_2_1X1.webp';
export function PersonalizaDownsellPopup({
  isOpen,
  onClose,
  onAccept,
  onDecline
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isOpen) {
      // Scroll popup container to top so user sees it immediately
      requestAnimationFrame(() => {
        containerRef.current?.scrollTo({
          top: 0
        });
      });
    }
  }, [isOpen]);
  if (!isOpen) return null;
  return <AnimatePresence mode="wait" data-id="element-455">
      <div ref={containerRef} className="fixed inset-0 z-[100000] flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto" data-id="element-456">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-black/92 backdrop-blur-md pointer-events-none" data-id="element-457" />
        <motion.div initial={{
        opacity: 0,
        scale: 0.85,
        y: 30
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: -20
      }} transition={{
        type: 'spring',
        damping: 22,
        stiffness: 280
      }} className="relative w-full max-w-[400px] sm:max-w-[460px] z-10 my-4 sm:my-auto" style={{
        boxShadow: '0 0 80px rgba(212,175,55,0.25), 0 30px 60px rgba(0,0,0,0.7)'
      }} data-id="element-458">
          {/* Gold border wrapper */}
          <div className="absolute inset-0 rounded-2xl sm:rounded-3xl bg-gradient-to-b from-gold via-gold/40 to-gold/60 p-[2px]" data-id="element-459">
            <div className="w-full h-full rounded-2xl sm:rounded-3xl bg-[#070707]" data-id="element-460" />
          </div>

          {/* Background image — blurred */}
          <img src={BG_IMAGE} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover rounded-2xl sm:rounded-3xl opacity-10 blur-[6px] scale-110 pointer-events-none" data-id="element-461" />
          <div className="absolute inset-0 rounded-2xl sm:rounded-3xl bg-gradient-to-b from-[#070707]/60 via-[#070707]/85 to-[#070707]/95 pointer-events-none" data-id="element-462" />

          <div className="relative z-10" data-id="element-463">
            {/* Urgency Header */}
            <div className="bg-gradient-to-r from-red-600 via-red-500 to-orange-500 text-white text-center py-2.5 px-3 rounded-t-2xl sm:rounded-t-3xl" data-id="element-464">
              <div className="font-black text-xs sm:text-sm uppercase tracking-wider animate-pulse flex items-center justify-center gap-2" data-id="element-465">
                <Sparkles className="w-4 h-4" data-id="element-466" />
                ¡ESPERA! OFERTA ESPECIAL
                <Sparkles className="w-4 h-4" data-id="element-467" />
              </div>
            </div>

            {/* Title */}
            <div className="px-5 sm:px-7 pt-6 sm:pt-8 text-center" data-id="element-468">
              <h3 className="font-serif text-2xl sm:text-3xl font-black text-white mb-3 leading-tight" data-id="element-469">
                No dejes pasar la{' '}
                <span className="text-gold" data-id="element-470">experiencia completa</span>
              </h3>
              <p className="text-white/60 text-xs sm:text-sm mb-4" data-id="element-471">
                Antes de irte solo con la guía, mira esta oferta exclusiva
              </p>
              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 mb-2" data-id="element-472">
                <span className="flex items-center gap-1 text-gold text-[9px] sm:text-[10px] font-bold" data-id="element-473">
                  <CheckCircle2 className="w-3 h-3" data-id="element-474" /> 4 Bonos completos
                </span>
                <span className="flex items-center gap-1 text-gold text-[9px] sm:text-[10px] font-bold" data-id="element-475">
                  <CheckCircle2 className="w-3 h-3" data-id="element-476" /> Audios premium
                </span>
                <span className="flex items-center gap-1 text-gold text-[9px] sm:text-[10px] font-bold" data-id="element-477">
                  <CheckCircle2 className="w-3 h-3" data-id="element-478" /> Acceso de por vida
                </span>
              </div>
            </div>

            {/* Price */}
            <div className="px-5 sm:px-7 pt-4 text-center" data-id="element-479">
              <div className="bg-white/5 border border-gold/20 rounded-xl p-4 mb-5" data-id="element-480">
                <p className="text-white/40 text-[10px] uppercase tracking-wider font-bold mb-2" data-id="element-481">
                  Precio exclusivo por tiempo limitado
                </p>
                <div className="flex items-center justify-center gap-3" data-id="element-482">
                  <span className="text-lg text-white/25 line-through font-bold" data-id="element-483">
                    $104.96
                  </span>
                  <span className="text-4xl sm:text-5xl font-black text-gold drop-shadow-[0_0_20px_rgba(212,175,55,0.4)]" data-id="element-484">
                    $19.76
                  </span>
                </div>
                <div className="inline-block bg-green-400/15 text-green-400 text-[9px] sm:text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider border border-green-400/20 mt-2" data-id="element-485">
                  AHORRAS $85.20 — 81% OFF
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="px-5 sm:px-7 pb-5 sm:pb-7" data-id="element-486">
              <motion.button onClick={onAccept} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.97
            }} className="w-full bg-gradient-to-r from-gold via-yellow-400 to-gold text-dark font-black text-xs sm:text-sm py-4 rounded-xl shadow-[0_10px_30px_rgba(212,175,55,0.35)] flex items-center justify-center gap-2 uppercase tracking-wider mb-3" data-id="element-487">
                SÍ, QUIERO TODO POR $19.76
                <ChevronRight className="w-5 h-5" data-id="element-488" />
              </motion.button>

              <button onClick={onDecline} className="w-full text-center text-white/25 hover:text-white/50 text-[10px] sm:text-xs underline transition-colors tracking-wide py-2" data-id="element-489">
                No gracias, continuar solo con la guía por $14.39
              </button>

              <button onClick={onClose} className="w-full flex items-center justify-center gap-1.5 text-white/40 hover:text-gold text-xs sm:text-sm font-bold uppercase tracking-wider transition-colors mt-3 py-2" data-id="element-490">
                ← Regresar
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}